import AuthSubmit from "components/v2/Auth/AuthSubmit";
import RegistrationFields from "./RegistrationFields";
import RegistrationTerms from "./RegistrationTerms";
import useRegisterIndividual from "api/UserResource/useRegisterIndividual";
import SocialLoginButtons from "components/v2/Auth/SocialLoginButtons";
import { Form, Formik } from "formik";
import { MAX_IMAGE_SIZE, phoneRegex } from "utils";
import * as Yup from "yup";
import { useRef, useState } from "react";
import { createUsername } from "./register_utils";

const Individual = () => {
	const [captchaValue, setCaptchaValue] = useState("");
	const recaptchaRef = useRef(null);
	const initialValues = {
		email: "",
		username: "",
		mobile_number: "",
		password: "",
		password_confirmation: "",
		first_name: "",
		last_name: "",
		dob: "",
		sex: "",
		like_notifications: false,
		terms: false,
		avatar: null,
		role: "Individual",
	};

	const registerSchema = Yup.object().shape({
		username: Yup.string().required("Username is required"),
		email: Yup.string().required("Email is required").email("Invalid email address"),
		terms: Yup.boolean().isTrue("Verify that you have read the terms and conditions"),
		mobile_number: Yup.string()
			.required("Mobile number is required")
			.matches(phoneRegex, "Invalid phone number")
			.min(10, "Mobile number must have at least characters")
			.max(20, "Mobile number cannot exceed 20 characters"),
		password: Yup.string()
			.min(6, "At least 6 characters required")
			.matches(/[a-zA-Z]/, "Must have at least one alphabet character")
			.matches(/[0-9]/, "Must have at least one digit")
			.required("Password required"),
		password_confirmation: Yup.string()
			.oneOf([Yup.ref("password")], "Passwords didn't match")
			.required("Confirmation required"),
		first_name: Yup.string().required("First name is required"),
		last_name: Yup.string().required("Surname is required"),
		sex: Yup.string().required("Gender is required"),
		dob: Yup.string().matches(
			/^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$/,
			{ message: "Invalid date format", excludeEmptyString: true },
		),
		avatar: Yup.mixed()
			.nullable()
			.test("fileSize", `Image size should not exceed ${MAX_IMAGE_SIZE}MB`, (value) => {
				if (!value) return true;
				return value.size / 1024 / 1024 <= MAX_IMAGE_SIZE;
			}),
	});

	const { mutateAsync, isLoading } = useRegisterIndividual();
	const handleRegister = async (data) => {
		const emailPrefix = `${data.first_name}.${data.last_name}`;
		const emailAddress =
			data.email?.length > 0 ? data.email : `${createUsername(emailPrefix)}@gmail.com`;
		try {
			await mutateAsync({
				...data,
				email: emailAddress,
				like_notifications: data.like_notifications ? 1 : 0,
				captcha: captchaValue,
				client: "web",
			});
		} catch {
			recaptchaRef.current?.reset();
			setCaptchaValue("");
		}
	};

	return (
		<>
			<SocialLoginButtons dividerPlacement="below" />
			<Formik initialValues={initialValues} onSubmit={handleRegister} validationSchema={registerSchema}>
				<Form className="auth-registration" noValidate>
					<RegistrationFields />
					<RegistrationTerms />
					<AuthSubmit
						captchaRef={recaptchaRef}
						captchaValue={captchaValue}
						onCaptchaChange={(value) => setCaptchaValue(value || "")}
						isLoading={isLoading}
						label="Create account"
						loadingLabel="Creating your account…"
					/>
				</Form>
			</Formik>
		</>
	);
};

export default Individual;
